import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { DEFAULT_COLORS } from '@toasttab/sites-components'
import classnames from 'classnames'
import { CountryCode, isValidPhoneNumber } from 'libphonenumber-js'

import {
  DiningOptionBehavior,
  LoyaltyFeatures
} from 'src/apollo/onlineOrdering'
import { ButtonType, MenuConfig } from 'src/apollo/sites'
import { useIsIntlRestaurant } from 'src/lib/js/hooks/useIsIntlRestaurant'
import useTracker from 'src/lib/js/hooks/useTracker'
import { TrackingEvents } from 'src/shared/components/common/tracking/TrackingEvents'
import { overrideConfigBasedOnTemplate } from 'src/public/components/default_template/menu_section/menuStylingOverrides'
import { useThemeColorScheme } from 'src/public/components/default_template/meta/useTheme'
import { PwlessAuth } from 'src/public/components/default_template/online_ordering/account/pwlessAuth/PwlessAuth'
import {
  CAInfo,
  ToastTOSAndPrivacy
} from 'src/public/components/default_template/online_ordering/account/pwlessAuth/legalUtils'
import { AuthenticationSource } from 'src/public/components/default_template/online_ordering/checkout/checkoutUtils'
import { useCustomer } from 'src/public/components/online_ordering/CustomerContextCommon'
import { useLoyalty } from 'src/public/components/online_ordering/LoyaltyContext'
import Image from 'src/shared/components/common/Image'
import { BrandedButton } from 'src/shared/components/common/button'
import {
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  useModal
} from 'src/shared/components/common/modal'
import { useRestaurant } from 'src/shared/components/common/restaurant_context/RestaurantContext'
import { alertError, alertSuccess } from 'src/shared/js/alertUtils'
import { ScreenWidth, useIsMobile } from 'src/shared/js/utils/WindowContext'
import { t, Trans } from 'src/translations'

import { LoadingSpinner } from 'shared/components/common/loading_spinner/LoadingSpinner'

import {
  LoyaltyProgramDetails,
  LoyaltySignupModal
} from 'public/components/default_template/offers/LoyaltySignupModal'
import {
  LoyaltyIcon,
  SmallRightArrow,
  LoyaltyDiamondIcon
} from 'public/components/default_template/offers/OffersIcons'

const getLoyaltyFeatureType = (
  loyaltyFeatures: (LoyaltyFeatures | null)[] | undefined | null,
  type: string
) => {
  return loyaltyFeatures?.find((feature) => feature?.tags?.includes(type))
}

export enum LoyaltyCardType {
  AccrualBonus = 'AccrualBonus',
  OrderConfirmationPage = 'OrderConfirmationPage',
  Signup = 'Signup'
}

export const LoyaltyCard = ({
  type = LoyaltyCardType.Signup,
  completedOrderBehavior,
  overrideColors
}: {
  type?: LoyaltyCardType
  completedOrderBehavior?: DiningOptionBehavior
  overrideColors?: {
    illustrationColor?: string
    backgroundColor?: string
    textColor?: string
    borderColor?: string
  }
}) => {
  const {
    restaurant: { config, ooConfig, meta },
    ooRestaurant,
    ooPromoBanners
  } = useRestaurant()
  const { track } = useTracker()
  const templateOverrides = useMemo(
    () => overrideConfigBasedOnTemplate(config.ooConfig as MenuConfig, config),
    [config]
  )
  const getColorFromTheme = useThemeColorScheme()
  const {
    isOpen: isLoyaltyModalOpen,
    onClose: onCloseLoyaltyModal,
    onOpen: onOpenLoyaltyModal
  } = useModal()
  const {
    isOpen: isAuthModalOpen,
    onClose: onCloseAuthModal,
    onOpen: onOpenAuthModal
  } = useModal()
  const [isShowingLegal, setIsShowingLegal] = useState(false)
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const loyaltyConfig = ooRestaurant?.loyaltyConfig
  const {
    loyaltyFeatures,
    programDescription,
    hasLoyaltyAccount,
    signupForLoyalty,
    loadingLoyaltyFeatures,
    loadingLoyaltyAccount,
    loyaltyLoading,
    pointsBalance
  } = useLoyalty()
  const { customer } = useCustomer()
  const [showSuccessfulSignupCard, setShowSuccessfulSignupCard] =
    useState(false)
  const isSignedIn = useMemo(() => !!customer, [customer])
  const accrualMessage = useMemo(
    () => getLoyaltyFeatureType(loyaltyFeatures, 'ACCRUAL'),
    [loyaltyFeatures]
  )
  const signUpBonus = useMemo(
    () => getLoyaltyFeatureType(loyaltyFeatures, 'SIGNUP'),
    [loyaltyFeatures]
  )

  // captures `## bonus point(s)`/ `## bonus visit(s)`
  const signUpBonusPoints = useMemo(
    () => signUpBonus?.message?.match(/((\d+)\s+bonus\s+(\S+))/m)?.[0],
    [signUpBonus?.message]
  )
  const [hasFinishedAuth, setHasFinishedAuth] = useState(false)
  const loyaltyBanner = ooPromoBanners?.loyaltyBanners?.[0]

  const [illustrationColor, backgroundColor, textColor, borderColor] =
    overrideColors
      ? [
          overrideColors.illustrationColor,
          overrideColors.backgroundColor,
          overrideColors.textColor,
          overrideColors.borderColor
        ]
      : getColorFromTheme(
          (theme) => {
            return [
              theme.colorOverrides?.offerColorOverrides?.illustration ??
                theme.colorScheme.background.primary,
              theme.colorOverrides?.onlineOrderingMenu?.cardBackground ??
                theme.colorScheme.surface.secondary,
              theme.colorOverrides?.onlineOrderingMenu?.itemTitle ??
                theme.colorScheme.text.default,
              theme.colorOverrides?.onlineOrderingMenu?.cardBorder ??
                theme.colorScheme.border.default
            ]
          },
          [
            ooConfig?.promoCodeConfig?.backgroundColor ??
              meta.primaryColor ??
              DEFAULT_COLORS.primary,
            config.ooConfig?.colors?.background ??
              DEFAULT_COLORS.background ??
              '#FFFFFF',
            config.ooConfig?.colors?.primaryText ?? DEFAULT_COLORS.text,
            templateOverrides?.borderColor
          ]
        )

  const isAccrualBonusCard = type === LoyaltyCardType.AccrualBonus
  const isOrderConfirmationPage = type === LoyaltyCardType.OrderConfirmationPage
  const isDelivery = completedOrderBehavior === DiningOptionBehavior.Delivery

  const headerText = useMemo(() => {
    if (isAccrualBonusCard && loyaltyBanner) {
      return t('loyalty-card.right-now', {
        bannerText: ooPromoBanners?.loyaltyBanners[0]?.bannerText
      })
    }
    if (!isOrderConfirmationPage) {
      return accrualMessage?.message
    }
    if (showSuccessfulSignupCard) {
      return t('loyalty-card.sign-up-successful')
    }
    return isSignedIn && hasLoyaltyAccount
      ? t('loyalty-card.you-have-points', { pointsBalance })
      : t('loyalty-card.dont-miss-out')
  }, [
    isAccrualBonusCard,
    loyaltyBanner,
    isOrderConfirmationPage,
    showSuccessfulSignupCard,
    isSignedIn,
    hasLoyaltyAccount,
    pointsBalance,
    ooPromoBanners?.loyaltyBanners,
    accrualMessage?.message
  ])

  const subHeaderText = useMemo(() => {
    if (isAccrualBonusCard && loyaltyBanner) {
      return t('loyalty-card.place-an-order')
    }
    if (showSuccessfulSignupCard) {
      return t('loyalty-card.rewards-points-earned', { pointsBalance })
    }
    if (isSignedIn && hasLoyaltyAccount) {
      return isOrderConfirmationPage
        ? isDelivery
          ? t('loyalty-card.earn-delivered')
          : t('loyalty-card.earn-picked-up')
        : t('loyalty-card.view-and-redeem')
    }
    if (signUpBonus) {
      // Removes the period at the end that is added by the loyalty API
      return signUpBonus.message?.replace(/\./g, '')
    }
    return t('loyalty-card.sign-up')
  }, [
    isAccrualBonusCard,
    loyaltyBanner,
    showSuccessfulSignupCard,
    isSignedIn,
    hasLoyaltyAccount,
    signUpBonus,
    pointsBalance,
    isOrderConfirmationPage,
    isDelivery
  ])

  const onClick = useCallback(() => {
    if (isSignedIn) {
      onOpenLoyaltyModal()
    } else {
      track(TrackingEvents.STARTED_AUTHENTICATION, {
        source: isOrderConfirmationPage
          ? AuthenticationSource.LoyaltyCardOnConfirmationPage
          : AuthenticationSource.LoyaltyCard
      })
      onOpenAuthModal()
    }
  }, [
    isSignedIn,
    onOpenLoyaltyModal,
    onOpenAuthModal,
    isOrderConfirmationPage,
    track
  ])

  const onCloseAuthModalCb = useCallback(() => {
    if (!hasFinishedAuth) {
      onCloseAuthModal()
    }
  }, [hasFinishedAuth, onCloseAuthModal])

  useEffect(() => {
    if (hasFinishedAuth && isSignedIn && !loadingLoyaltyAccount) {
      setHasFinishedAuth(false)
      onCloseAuthModal()
      alertSuccess(
        hasLoyaltyAccount
          ? t('loyalty-card.welcome-back-continue', {
              name: customer?.firstName
            })
          : t('loyalty-card.welcome-back', { name: customer?.firstName }),
        { containerId: 'overModals' }
      )
      // if we're on the order confirmation page, only open the loyalty modal if we need to sign up still
      if (isOrderConfirmationPage && hasLoyaltyAccount) return
      onOpenLoyaltyModal()
    }
  }, [
    customer?.firstName,
    hasLoyaltyAccount,
    isSignedIn,
    loadingLoyaltyAccount,
    onCloseAuthModal,
    onOpenLoyaltyModal,
    hasFinishedAuth,
    isOrderConfirmationPage
  ])

  const onCloseModal = useCallback(() => {
    setIsShowingLegal(false)
    setAcceptedTerms(false)
    onCloseLoyaltyModal()
  }, [onCloseLoyaltyModal])

  const signUpForLoyaltyCb = useCallback(async () => {
    if (customer) {
      // Show error if customer phone number and rx country don't match,
      // since loyalty currently only works with local numbers,
      if (
        customer?.phone &&
        ooRestaurant?.i18n.country &&
        !isValidPhoneNumber(
          customer.phone,
          ooRestaurant.i18n.country as CountryCode
        )
      ) {
        alertError(t('loyalty-card.alert-error-one'), {
          containerId: 'overModals'
        })
        return
      }

      const { data } = await signupForLoyalty(customer.id)
      if (data?.loyaltySignup.__typename === 'LoyaltySignupResponse') {
        alertSuccess(
          signUpBonus
            ? t('loyalty-card.sign-up-earned', { signUpBonusPoints })
            : t('loyalty-card.sign-up-loyalty'),
          { containerId: 'overModals' }
        )
        onCloseModal()
        setShowSuccessfulSignupCard(true)
      } else {
        alertError(t('loyalty-card.alert-error-two'), {
          containerId: 'overModals'
        })
      }
    }
  }, [
    customer,
    onCloseModal,
    ooRestaurant?.i18n?.country,
    signUpBonus,
    signUpBonusPoints,
    signupForLoyalty
  ])

  const [loyaltyModalHeight, setLoyaltyModalHeight] = useState(0)
  const loyaltyModalRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (isLoyaltyModalOpen) {
      setLoyaltyModalHeight(
        Math.max(loyaltyModalHeight, loyaltyModalRef.current?.clientHeight || 0)
      )
    }
  }, [isLoyaltyModalOpen, loyaltyModalHeight])

  if (
    !loyaltyConfig?.loyaltySignupEnabled ||
    !accrualMessage ||
    loadingLoyaltyFeatures ||
    !programDescription ||
    (isAccrualBonusCard && (!isSignedIn || !hasLoyaltyAccount))
  ) {
    return null
  }

  if (isOrderConfirmationPage && loyaltyLoading) {
    return (
      <div
        className='orderConfirmationSection loyaltySection pdfHide'
        data-testid='loyalty-section'
      >
        <div className='loyaltyLoading' data-testid='loyalty-loading-spinner'>
          <LoadingSpinner color={illustrationColor} />
        </div>
      </div>
    )
  }

  const content = (
    <>
      <button
        className={classnames(
          'styledCard',
          templateOverrides.borderStroke,
          templateOverrides?.roundedCorner,
          templateOverrides?.dropShadow
        )}
        style={{
          borderColor,
          backgroundColor,
          color: textColor,
          background: `linear-gradient(270deg, ${illustrationColor}22 5%, ${backgroundColor} 25%)`
        }}
        onClick={onClick}
        disabled={isOrderConfirmationPage && isSignedIn && hasLoyaltyAccount}
        aria-disabled={
          isOrderConfirmationPage && isSignedIn && hasLoyaltyAccount
        }
        aria-labelledby='loyalty-card-label'
        data-testid={'loyalty-offer-card'}
      >
        <div className='cardContent'>
          <div className='cardInfo' id='loyalty-card-label'>
            <div className='cardText' data-testid='offer-text'>
              {headerText}
            </div>
            <div className='cardDetails'>
              <span>{subHeaderText}</span>
              {!hasLoyaltyAccount && <SmallRightArrow color={textColor} />}
            </div>
          </div>
          <div
            className='illustration'
            data-testid={'offer-illustration-loyalty'}
          >
            {isAccrualBonusCard ? (
              <div className='scaledIllustrationContainer'>
                <LoyaltyDiamondIcon color={illustrationColor} />
              </div>
            ) : (
              <LoyaltyIcon color={illustrationColor} />
            )}
          </div>
        </div>
      </button>
      <Modal
        className='offerModalWrapper'
        isOpen={isLoyaltyModalOpen}
        onClose={onCloseModal}
      >
        <ModalOverlay fadeIn fadeOut />
        {isShowingLegal ? (
          <TermsAndConditionsModal
            closeLegal={() => setIsShowingLegal(false)}
            modalHeight={loyaltyModalHeight}
            onAgree={() => {
              setAcceptedTerms(true)
              setIsShowingLegal(false)
            }}
          />
        ) : (
          <LoyaltySignupModal
            illustrationColor={illustrationColor}
            openLegal={() => setIsShowingLegal(true)}
            acceptedTerms={acceptedTerms}
            setAcceptedTerms={setAcceptedTerms}
            signupForLoyalty={signUpForLoyaltyCb}
            contentRef={loyaltyModalRef}
          />
        )}
      </Modal>
      <Modal isOpen={isAuthModalOpen} onClose={onCloseAuthModalCb}>
        <ModalOverlay />
        <PwlessAuth
          source={
            isOrderConfirmationPage
              ? AuthenticationSource.LoyaltyCardOnConfirmationPage
              : AuthenticationSource.LoyaltyCard
          }
          skipLoginToast={true}
          customLoginHeader={t('loyalty-card.get-started')}
          secondaryCTAText={t('loyalty-card.loyalty-program-details')}
          secondaryContent={
            <LoyaltyProgramDetails illustrationColor={illustrationColor} />
          }
          onSuccessfulLogin={() => setHasFinishedAuth(true)}
        />
      </Modal>
    </>
  )

  return isOrderConfirmationPage ? (
    <div
      className='orderConfirmationSection loyaltySection pdfHide'
      data-testid='loyalty-section'
    >
      {content}
    </div>
  ) : (
    content
  )
}

const TermsAndConditionsModal = ({
  closeLegal,
  onAgree,
  modalHeight
}: {
  closeLegal: () => void
  onAgree: () => void
  modalHeight: number
}) => {
  const isIntlRestaurant = useIsIntlRestaurant()
  const isMobile = useIsMobile(ScreenWidth.EXTRA_SMALL)
  return (
    <ModalContent
      contentClassName='modalContentV2 loyalty fullScreenMobile'
      style={!isMobile ? { height: `${modalHeight}px` } : undefined}
    >
      <div className='modalHeader withHeaderIconRight'>
        <button
          aria-label={t('loyalty-card.return-to-loyalty-sign-up')}
          className='iconRight'
          data-testid='legal-back-button'
          onClick={closeLegal}
        >
          <Image
            alt={t('loyalty-card.alt-back')}
            src='icons/arrow-left-gray.svg'
          />
        </button>
        <ModalCloseButton />
      </div>
      <div className='body terms'>
        <div className='headerContent centered'>
          <div className='title'>{t('loyalty-card.terms-and-conditions')}</div>
        </div>
        <p data-testid='loyalty-legal-text' className='termsText'>
          <LoyaltyTermsAndConditions isIntlRestaurant={isIntlRestaurant} />
        </p>
        <BrandedButton
          variant={ButtonType.Primary}
          onClick={onAgree}
          className='termsButton'
        >
          {t('loyalty-card.i-agree')}
        </BrandedButton>
      </div>
    </ModalContent>
  )
}

export const LoyaltyTermsAndConditions = ({
  isIntlRestaurant
}: {
  isIntlRestaurant: boolean
}) => {
  return (
    <>
      {isIntlRestaurant ? (
        <Trans
          i18nKey='loyalty-card.terms-intl'
          components={{ ToastTOSAndPrivacy: <ToastTOSAndPrivacy /> }}
        />
      ) : (
        <Trans
          i18nKey='loyalty-card.terms-domestic'
          components={{
            ToastTOSAndPrivacy: <ToastTOSAndPrivacy />,
            CAInfo: <CAInfo />
          }}
        />
      )}
    </>
  )
}
